import { useCallback, useEffect, useState, type ChangeEvent } from 'react';

import { utilities } from 'core';
import { useUpdateEffect } from 'ui/hooks';

import { decrement, increment, getAllowedValue } from './QuantitySelector.helpers';

import type { QuantitySelectorState } from './QuantitySelector.d';

const useQuantitySelector = ({ max, min, onChange, value }: QuantitySelectorState) => {
  const [currentValue, setCurrentValue] = useState<number | undefined>();

  const handleDecrement = useCallback(() => {
    if (currentValue) {
      setCurrentValue(decrement(currentValue, min));
    }
  }, [currentValue, min]);

  const handleIncrement = useCallback(() => {
    if (currentValue) {
      setCurrentValue(increment(currentValue, max));
    }
  }, [currentValue, max]);

  const setValue = useCallback(
    (inputValue: string) => {
      setCurrentValue(getAllowedValue({ max, min, value: inputValue }));
    },
    [max, min],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value: inputValue } = event.currentTarget;

      setValue(inputValue);
    },
    [setValue],
  );

  const handleOnChangeCallback = useCallback(
    utilities.debounce((inputValue: number) => {
      if (onChange) {
        onChange(inputValue);
      }
    }, 1500),
    [],
  );

  useUpdateEffect(() => {
    if (currentValue !== value) {
      handleOnChangeCallback(currentValue);
    }
  }, [currentValue, handleOnChangeCallback]);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  return {
    currentValue,
    handleChange,
    handleDecrement,
    handleIncrement,
  };
};

export default useQuantitySelector;
